import { type PlanBuildData, type PlanConfig, type PlanStop, type StopBuilder, ITEMS_PER_MINUTE } from "../plan-template.js";
import { weakestItems } from "../scheduler.js";
import { type CurriculumNode } from "../item-library.js";
import { nodeToDrillRoute, shuffled } from "./_helpers.js";

export const strengthenStop: StopBuilder = (
  data: PlanBuildData,
  config: PlanConfig,
  alreadyPlanned: ReadonlySet<string>,
): PlanStop | null => {
  const { items, cards, nodes, reviews, now } = data;
  const {
    warmupItemCap,
    warmupLapseThreshold,
    warmupLapseMinCount,
    relistenLapseThreshold,
    lapseWindowMs,
  } = config;

  const schedulableItems = items.filter(
    (i) =>
      data.unlockedNodeSet.has(i.nodeId) &&
      data.engagedNodeSet.has(i.nodeId) &&
      data.practiceReviewedNodeSet.has(i.nodeId) &&
      !alreadyPlanned.has(i.id),
  );

  const cutoff = new Date(now.getTime() - lapseWindowMs);
  let bestNode: CurriculumNode | null = null;
  let bestRate = 0;

  for (const node of nodes) {
    const nodeItemIds = new Set(
      schedulableItems.filter((i) => i.nodeId === node.id).map((i) => i.id),
    );
    if (nodeItemIds.size === 0) continue;
    const recent = reviews.filter(
      (r) => nodeItemIds.has(r.itemId) && new Date(r.timestamp) >= cutoff,
    );
    if (recent.length === 0) continue;
    const lapses = recent.filter((r) => !r.correct).length;
    const rate = lapses / recent.length;
    if (rate >= warmupLapseThreshold && lapses >= warmupLapseMinCount && rate > bestRate) {
      bestNode = node;
      bestRate = rate;
    }
  }

  if (bestNode) {
    const nodeItems = shuffled(
      schedulableItems.filter((i) => i.nodeId === bestNode!.id),
    ).slice(0, warmupItemCap);
    const pct = Math.round(bestRate * 100);
    const relistenNudge =
      bestRate >= relistenLapseThreshold
        ? { suggestRelisten: true, suggestRelistenRoute: nodeToDrillRoute(bestNode.id) }
        : {};
    return {
      kind: "strengthen",
      title: bestNode.title,
      reason: `You missed ${pct}% of ${bestNode.title} items last week. Extra reps will pull them back.`,
      itemCount: nodeItems.length,
      estimatedMinutes: Math.max(1, Math.round(nodeItems.length / ITEMS_PER_MINUTE)),
      items: nodeItems,
      ...relistenNudge,
    };
  }

  const weak = weakestItems(schedulableItems, cards, 4, now);
  if (weak.length === 0) return null;
  return {
    kind: "strengthen",
    title: "Weakest items",
    reason: `Skills most likely to slip from memory right now.`,
    itemCount: weak.length,
    estimatedMinutes: Math.max(1, Math.round(weak.length / ITEMS_PER_MINUTE)),
    items: weak,
  };
};
